import { ROUTES } from '@sportninja/common/constants/app'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import type { Country } from '@sportninja/common/types/Country'
import type { BillingScheduleItem } from '@sportninja/common/types/Registration'

const SESSION_STATUS_ID = 6
export const CANCELLED_STATUS_ID = 5

export interface RegistrationDetailsPathRecord {
  uid: string
  schedule: { uid: string }
  is_official_budget_line?: boolean | null
}

/**
 * Where a My Registrations row opens. SN-8480 — a Team Rep's own Team Budget line
 * has nothing to show on Info (no player, no form), so it opens on its Billing
 * Schedule; every other registration opens on Info.
 */
export const getRegistrationDetailsPath = (
  record: RegistrationDetailsPathRecord
): string => {
  const tab = record.is_official_budget_line ? 'billing' : 'info'

  return `${ROUTES.REGISTRATION_DETAILS.ROOT}/${record.schedule.uid}/${tab}?registration_id=${record.uid}`
}

export const isRegistrationSession = (record: UserRegistration): boolean =>
  record.status_id === SESSION_STATUS_ID ||
  record.type === 'registration_session'

export const isRegistrationNameClickable = (
  record: UserRegistration
): boolean => isRegistrationSession(record) || (record.billing?.length ?? 0) > 0

/** The billing lines this registration's own payer owes. */
export const getApplicableBilling = (
  record: UserRegistration
): BillingScheduleItem[] => {
  const userType = record.registration_type === 'Team' ? 'teamRep' : 'player'

  return record.billing?.filter((item) => item.user_type === userType) ?? []
}

export const getRegistrationCountry = (
  record: UserRegistration
): Country | undefined =>
  record.currency_country || record.billing?.[0]?.currency_country

export const getOutstandingAmount = (record: UserRegistration): number => {
  if (record.is_comp) {
    return 0
  }

  return getApplicableBilling(record)
    .filter((item) => !item.paid)
    .reduce((total, item) => total + item.subtotal, 0)
}

export const getTotalPaid = (record: UserRegistration): number =>
  getApplicableBilling(record)
    .filter((item) => item.paid)
    .reduce((total, item) => total + item.display_subtotal, 0)

export const getPendingPayments = (
  record: UserRegistration
): BillingScheduleItem[] =>
  getApplicableBilling(record).filter((item) => !item.paid)

export interface RefundSummary {
  count: number
  total: number
  reason: string | null
  note: string | null
  transactionId: string | null
}

/** What a registration's refunds add up to, or null when it has none. */
export const getRefundSummary = (
  record: UserRegistration
): RefundSummary | null => {
  const refunded = getApplicableBilling(record).filter(
    (item) => item.refunded || (item.charge && !!item.charge.refunded)
  )
  if (refunded.length === 0) {
    return null
  }

  return {
    count: refunded.length,
    total: refunded.reduce((total, item) => {
      const byField = Number(item.refund_subtotal || 0)
      const byCharge = Number(item.charge?.refund_amount || 0)
      return total + (byField > 0 ? byField : byCharge)
    }, 0),
    reason:
      refunded.find((item) => item.charge?.refund_reason)?.charge
        ?.refund_reason ?? null,
    note:
      refunded.find((item) => item.charge?.refund_note)?.charge?.refund_note ??
      null,
    transactionId:
      refunded.find((item) => item.charge?.stripe_id)?.charge?.stripe_id ??
      null,
  }
}

export interface PaymentDestination {
  url: string
  newTab: boolean
}

/**
 * Where Pay goes: one pending payment opens straight on that payment in a new
 * tab, several open the Billing tab so the payer can choose.
 */
export const getPaymentDestination = (
  record: UserRegistration
): PaymentDestination => {
  const pending = getPendingPayments(record)

  if (pending.length === 1) {
    const teamId = record.team?.uid
    const rosterId = record.team?.team_player_roster_id

    return {
      url: `${ROUTES.REGISTRATION_PAYMENT}?teamId=${teamId}&rosterId=${rosterId}&billingId=${pending[0].uid}`,
      newTab: true,
    }
  }

  return {
    url: `/registration-details/${record.schedule.uid}/billing?registration_id=${record.uid}`,
    newTab: false,
  }
}

/** Rows without a team name (sessions still in progress) sort last. */
export const compareRegistrationNames = (
  a: string | null | undefined,
  b: string | null | undefined
): number => {
  if (!a && !b) {
    return 0
  }
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }
  return a.localeCompare(b)
}

export const compareRegistrationDates = (a: string, b: string): number =>
  new Date(a).getTime() - new Date(b).getTime()

/** The Competition filter's choices: each competition once, in row order. */
export const getCompetitionFilterOptions = (
  registrations: UserRegistration[]
): { label: string; value: string }[] => {
  const names = registrations
    .map((registration) => registration?.schedule?.name)
    .filter(Boolean)

  return [...new Set(names)].map((name) => ({ label: name, value: name }))
}
